import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { FIRST_PAGE, MAX_PER_PAGE } from 'constants/constants';
import { GeneratedContentPage, QueryGeneratedContentsArgs } from 'gql/graphql';
import { gql } from 'graphql-request';
import { TGenericError } from 'types/TGenericError';
import { buildQueryKey } from 'utils/buildQueryKey';
import clientUtil from 'utils/clientUtil';

type TVariables = QueryGeneratedContentsArgs;
type TData = GeneratedContentPage;

const MOCK: TVariables = {
  contentTypeFilter: undefined,
  id: undefined,
  languageFilter: undefined,
  page: FIRST_PAGE,
  perPage: MAX_PER_PAGE,
  query: undefined,
  skipPagination: undefined,
  sortAttribute: undefined,
  sortOrder: undefined
};

export const useContentGenerationData = (
  variables: TVariables = MOCK,
  options: Omit<UseQueryOptions<unknown, TGenericError, TData>, 'queryKey'> = {}
) => {
  const client = clientUtil();

  const graphql = gql`
    query generatedContents(
      $id: ID
      $query: String
      $sortAttribute: GeneratedContentSortAttributeEnum
      $sortOrder: SortOrderEnum
      $languageFilter: [OrderableTargetLanguageCodesEnum!]
      $contentTypeFilter: GeneratedContentTypeEnum
      $page: Int
      $perPage: Int
    ) {
      generatedContents(
        id: $id
        query: $query
        sortAttribute: $sortAttribute
        sortOrder: $sortOrder
        languageFilter: $languageFilter
        contentTypeFilter: $contentTypeFilter
        page: $page
        perPage: $perPage
      ) {
        pagesCount
        nodesCount
        nodes {
          additionalInstructions
          contentFormat
          contentLength
          contentType
          createdAt
          generations {
            content
            id
            targetLanguage
            words
            tokens
          }
          uploadedFiles {
            filename
          }
          id
          languages
          status
          styleGuide {
            culturalConsideration
            formality
            purposeOfText
            style
            targetAudience
            toneOfVoice
          }
          topic
          title
          words
          tokens
        }
      }
    }
  `;

  return useQuery<unknown, TGenericError, TData>({
    ...options,
    queryKey: ['useContentGenerationData', ...buildQueryKey({ ...MOCK, ...variables })],
    queryFn: async () => {
      return client.request({
        data: {
          gql: graphql,
          variables
        }
      });
    }
  });
};
